import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LIBRARY_TOUR_CLASSES } from '@/constants/tour/classNames';

import { DocumentTab } from './DocumentsTab';
import { FranchiseContactsTab } from './FranchiseContactsTab';

type TabValue = 'documents' | 'franchise-contacts';

const TAB_OPTIONS: { value: TabValue; label: string }[] = [
  { value: 'documents', label: 'Documents' },
  { value: 'franchise-contacts', label: 'Franchise Contacts' },
];

const DEFAULT_TAB: TabValue = 'documents';

const DashboardLibrary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState<TabValue>(DEFAULT_TAB);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newDefaultTab = (queryParams.get('tab') as TabValue) || DEFAULT_TAB;
    setSelectedTab(newDefaultTab);
  }, [location]);

  const handleClickTab = (value: TabValue): void => {
    setSelectedTab(value);
    navigate(`/dashboard/library?tab=${value}`);
  };

  return (
    <div className="space-y-4">
      <div className={LIBRARY_TOUR_CLASSES.PAGE_HEADER}>
        <h2 className="as-strong">Company Directory</h2>
        <p className="as-body-02 as-strong text-typo-gray-tertiary">
          Documents and attachments that have been uploaded by franchisors.
        </p>
      </div>
      <Tabs
        value={selectedTab}
        defaultValue={DEFAULT_TAB}
        onValueChange={(value) => handleClickTab(value as TabValue)}
      >
        <TabsList
          className={`${LIBRARY_TOUR_CLASSES.MAIN_TABS} mb-4 h-auto max-w-full justify-start overflow-x-auto max-md:w-full md:flex-wrap`}
        >
          {TAB_OPTIONS.map(({ value, label }) => (
            <TabsTrigger
              key={value}
              value={value}
              className={`flex-1 ${value === 'documents' ? LIBRARY_TOUR_CLASSES.DOCUMENTS_TAB : LIBRARY_TOUR_CLASSES.CONTACTS_TAB}`}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value="documents"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <DocumentTab />
        </TabsContent>
        <TabsContent
          value="franchise-contacts"
          className="rounded-xl border border-alpha-dark-5 bg-muted p-4"
        >
          <FranchiseContactsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export { DashboardLibrary };
